/**
 * animations.js
 *
 * Shared Framer Motion variants and transition configs for the entire app.
 * Import what you need — tree-shaking keeps the bundle lean.
 *
 * Timing conventions (aligned with index.css tokens):
 *   Micro-interactions  : 150–300 ms  (hover, tap, focus)
 *   Content entrances   : 350–500 ms  (fade, slide, scale)
 *   Page transitions    : 400–600 ms  (full-screen enter/exit)
 */

// ─── Easings ──────────────────────────────────────────────────────────────────

/** Smooth deceleration — use for entrances */
export const EASE_OUT       = [0.25, 0.46, 0.45, 0.94]
/** Smooth acceleration — use for exits */
export const EASE_IN        = [0.55, 0.06, 0.68, 0.19]
/** Symmetric — use for page transitions and size changes */
export const EASE_IN_OUT    = [0.45, 0.05, 0.55, 0.95]
/** Anticipation curve — use for playful pops */
export const EASE_BACK_OUT  = [0.34, 1.56, 0.64, 1]

// ─── Page Transitions ─────────────────────────────────────────────────────────

/**
 * Wrap each page component with these variants so AnimatePresence in App.jsx
 * produces a clean fade + subtle upward slide between routes.
 *
 * App.jsx already has:
 *   <AnimatePresence mode="wait" initial={false}>
 *     <Routes location={location} key={location.pathname}>
 *
 * So just apply pageVariants to the outermost motion.div inside each page.
 */
export const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0  },
  exit:    { opacity: 0, y: -12 },
}

export const pageTransition = {
  duration: 0.45,
  ease: EASE_IN_OUT,
}

// ─── Fade Up ──────────────────────────────────────────────────────────────────

/**
 * Single element: fades in while rising 24 px into position.
 * Best for section headings, cards, and content blocks.
 */
export const fadeUp = {
  initial:  { opacity: 0, y: 24 },
  animate:  { opacity: 1, y: 0  },
  exit:     { opacity: 0, y: 12 },
  transition: { duration: 0.45, ease: EASE_OUT },
}

/**
 * Parent container that staggers its children with fadeUp.
 * Pair with staggerItem (or itemVariants) on each child.
 *
 * Usage:
 *   <motion.ul variants={fadeUpContainer} initial="initial" animate="animate">
 *     {items.map(i => <motion.li key={i.id} variants={staggerItem}>…</motion.li>)}
 *   </motion.ul>
 */
export const fadeUpContainer = {
  initial: {},
  animate: {
    transition: {
      staggerChildren:  0.08,
      delayChildren:    0.1,
    },
  },
  exit: {},
}

// ─── Fade In ──────────────────────────────────────────────────────────────────

/**
 * Simple opacity-only fade. Use for overlays, tooltips, and decorative elements
 * where movement would feel heavy.
 */
export const fadeIn = {
  initial:  { opacity: 0 },
  animate:  { opacity: 1 },
  exit:     { opacity: 0 },
  transition: { duration: 0.35, ease: EASE_OUT },
}

// ─── Slide In ─────────────────────────────────────────────────────────────────

/** Enters from the left — good for sidebars and drawer menus. */
export const slideInLeft = {
  initial:  { opacity: 0, x: -32 },
  animate:  { opacity: 1, x:   0 },
  exit:     { opacity: 0, x: -24 },
  transition: { duration: 0.4, ease: EASE_OUT },
}

/** Enters from the right — good for side panels and notifications. */
export const slideInRight = {
  initial:  { opacity: 0, x: 32 },
  animate:  { opacity: 1, x:  0 },
  exit:     { opacity: 0, x: 24 },
  transition: { duration: 0.4, ease: EASE_OUT },
}

// ─── Scale Pop ────────────────────────────────────────────────────────────────

/**
 * Scales up from 92 % while fading in. Great for modals, dropdowns, and cards
 * that should feel like they "pop" into existence.
 */
export const scalePop = {
  initial:  { opacity: 0, scale: 0.92 },
  animate:  { opacity: 1, scale: 1    },
  exit:     { opacity: 0, scale: 0.95 },
  transition: {
    type:      'spring',
    stiffness: 320,
    damping:   24,
  },
}

// ─── Stagger ──────────────────────────────────────────────────────────────────

/**
 * Generic stagger container — does not prescribe the child animation.
 * Children inherit their own variants (e.g. staggerItem, fadeUp, slideInLeft).
 */
export const staggerContainer = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.07,
      delayChildren:   0.05,
    },
  },
  exit: {
    transition: {
      staggerChildren:  0.04,
      staggerDirection: -1,
    },
  },
}

/**
 * Default stagger child: fade up 20 px.
 * Designed to be used inside staggerContainer or fadeUpContainer.
 */
export const staggerItem = {
  initial:  { opacity: 0, y: 20 },
  animate:  {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: EASE_OUT },
  },
  exit: {
    opacity: 0,
    y: 10,
    transition: { duration: 0.2, ease: EASE_IN },
  },
}

// ─── Float (infinite) ─────────────────────────────────────────────────────────

/**
 * Gentle infinite vertical float for hero illustrations, badges, or decorative
 * elements. Apply directly via the `animate` prop — NOT through variants — so
 * it can run independently from page entrance state.
 *
 * Usage:
 *   <motion.div animate={floatAnimation.animate} transition={floatAnimation.transition}>
 */
export const floatAnimation = {
  animate: {
    y: [0, -10, 0],
  },
  transition: {
    duration:   4,
    ease:       'easeInOut',
    repeat:     Infinity,
    repeatType: 'loop',
  },
}

// ─── Glow Pulse (infinite) ────────────────────────────────────────────────────

/**
 * Pulses the opacity of a glow layer between two values. Attach to an
 * absolutely-positioned sibling div that renders the glow background so the
 * animation never touches `box-shadow` (avoids paint cost).
 *
 * Usage:
 *   <motion.div animate={glowPulse.animate} transition={glowPulse.transition}
 *               className="absolute inset-0 rounded-xl bg-primary-500/20 blur-xl -z-10" />
 */
export const glowPulse = {
  animate: {
    opacity: [0.4, 0.85, 0.4],
    scale:   [1,   1.04, 1  ],
  },
  transition: {
    duration:   3,
    ease:       'easeInOut',
    repeat:     Infinity,
    repeatType: 'loop',
  },
}

// ─── Micro-interaction helpers ────────────────────────────────────────────────

/**
 * Spring config for interactive elements (hover/tap).
 * Snappy and physical — do not use for entrance animations.
 */
export const springSnappy = {
  type:      'spring',
  stiffness: 400,
  damping:   20,
}

/**
 * Spring config for slightly larger motions (modals, drawers).
 * Softer than springSnappy.
 */
export const springSoft = {
  type:      'spring',
  stiffness: 280,
  damping:   26,
}

// ─── Error Shake ──────────────────────────────────────────────────────────────

/**
 * Horizontal shake sequence for invalid form fields.
 * Drive with `animate` + `key` trick to re-trigger on each error:
 *
 *   <motion.div
 *     key={errorCount}          // increment key to replay
 *     animate={shakeAnimation.animate}
 *     transition={shakeAnimation.transition}
 *   >
 */
export const shakeAnimation = {
  animate: {
    x: [0, -8, 8, -6, 6, -3, 3, 0],
  },
  transition: {
    duration:   0.45,
    ease:       'easeInOut',
  },
}
